/**
 * The bar that says "your role changed since you signed in".
 *
 * A sign-in fixes what the API will let a person do: the authorizer re-checks
 * their live roles on every request but never grants beyond the role list the
 * access token was minted with. So a permission published for a role AFTER the
 * person signed in shows up in the published policy and nowhere else — the
 * session resolves it as `awaitingSignIn` (fe-commons `resolvePermissionsView`)
 * and keeps it out of what the UI offers.
 *
 * Without this bar the person sees exactly what a role that never had the
 * permission sees: a control that is missing or disabled. On 2026-10-01
 * `seed:create` was published for Admin mid-session and "Identify addresses"
 * failed for every signed-in admin, who had no way to tell that signing in
 * again was the whole fix.
 *
 * Renders nothing when nothing is waiting, or in an impersonated session —
 * signing in again there would end the impersonation, and the impersonated
 * token is reduced on purpose.
 */
import * as React from "react";
import { KeyRound } from "lucide-react";
import { getImpersonationState } from "@authe/fe-commons/auth";
import type { AutheClaims } from "@authe/fe-commons/auth";
import { useSession } from "@authe/ui-core/session";

/** How many waiting scopes the bar names before it says "and N more". */
const NAMED = 3;

/** "seed:create, pipelines:process and 4 more" — the scopes, briefly. */
export function waitingScopesPhrase(scopes: readonly string[]): string {
  const named = scopes.slice(0, NAMED);
  const rest = scopes.length - named.length;
  if (rest <= 0) {
    return named.length > 1 ? `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}` : named[0] ?? "";
  }
  return `${named.join(", ")} and ${rest} more`;
}

export function PermissionsChangedBanner(): React.ReactElement | null {
  const { session, login } = useSession();
  const [dismissed, setDismissed] = React.useState(false);
  const waiting = session?.permissions?.awaitingSignIn ?? [];
  if (dismissed || !waiting.length) return null;
  if (getImpersonationState(session?.claims as AutheClaims | undefined).active) return null;

  const count = waiting.length;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border bg-muted px-4 py-1.5 text-xs text-foreground print:hidden"
    >
      <KeyRound className="size-3.5 shrink-0" aria-hidden />
      <span>
        Your role was given {count === 1 ? "a permission" : `${count} permissions`} after you signed in (
        <span className="font-mono">{waitingScopesPhrase(waiting)}</span>). Sign in again to use{" "}
        {count === 1 ? "it" : "them"}.
      </span>
      <span className="ml-auto flex items-center gap-3">
        <button
          type="button"
          className="font-medium underline underline-offset-2 hover:no-underline"
          onClick={() => void login({ returnTo: window.location.href })}
        >
          Sign in again
        </button>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          onClick={() => setDismissed(true)}
        >
          Later
        </button>
      </span>
    </div>
  );
}
